import React, { useState, useEffect, SetStateAction, Dispatch } from 'react';

import { API_STATUS } from '../../types';
import { DefaultComponentProps, PartnerList, PartnerInfo, FormStatus, ValidationFeedback, CreateUserResultTypes, FormSubmitionValues } from '../../../types';
import { isEmpty, isOnlyNumbers } from '../../../validation';
import { forgotPin } from '../../api';
import { ariaInvalid } from '../../../helpers';

import { CardBody, CardFooter, CardHeader, PartnerSelect, Spinner, ValidationSummary } from '../../../components';

interface Props extends DefaultComponentProps {
  partnerInfo: PartnerInfo;
  setPartnerInfo: (partnerInfo: PartnerInfo) => void;
  partnerList: PartnerList;
  setView: (resultType: CreateUserResultTypes) => void;
  setTryAgainView: Dispatch<SetStateAction<CreateUserResultTypes>>;
  tryToSubmitAgain: () => void;
  formSubmitionValues: FormSubmitionValues;
  setFormSubmitionValues: Dispatch<SetStateAction<FormSubmitionValues>>;
}

export const ForgotPin = ({
    textConfig, partnerList, partnerInfo, setPartnerInfo, setView, setTryAgainView, tryToSubmitAgain,
    formSubmitionValues, setFormSubmitionValues }: Props) => {
  const { title, ingress, emoji, emojiLabel, buttonText, formCustomerNumberLabel } = textConfig.forgotPin;
  const { back } = textConfig.common;
  const { partnerNumberInvalid, customerNumberInvalid } = textConfig.validationSummary.formValidationFeedback;

  const [formStatus, setFormStatus] = useState(FormStatus.INITIAL);
  const [validationFeedback, setValidationFeedback] = useState({
    partner: partnerNumberInvalid,
    customerNumber: customerNumberInvalid,
  } as ValidationFeedback);

  useEffect(() => {
    setValidationFeedback(state => ({
      ...state,
      partner: partnerInfo.partnerNumber ? '' : partnerNumberInvalid,
      customerNumber: formSubmitionValues.customerNumber ? '' : customerNumberInvalid,
    }));
  }, [customerNumberInvalid, formSubmitionValues.customerNumber, partnerInfo.partnerNumber, partnerNumberInvalid]);

  const handleFormSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (validationFeedback.partner !== '' || validationFeedback.customerNumber !== '') {
      setFormStatus(FormStatus.TRIED_SUBMITTING);
      return;
    }

    setFormStatus(FormStatus.SUBMITTING);

    let customerNumber = formSubmitionValues.customerNumber;
    if (customerNumber.length > 8) { // supporting customerNumber with partnerId
      customerNumber = customerNumber.substring(customerNumber.length - 8);
    }

    forgotPin({ partnerid: partnerInfo.partnerNumber, customerid: customerNumber })
      .then((res) => {
        setTryAgainView(CreateUserResultTypes.LEGACY_FORGOT_PIN);
        if (res.status === API_STATUS.SUCCESS) {
          if (res.value) { // res.value "din" if user exists (e-mail address hidden, APP-13308)
            setTryAgainView(CreateUserResultTypes.USER_CREATE);
            setView(CreateUserResultTypes.LEGACY_PIN_SENT);
          } else {
            setView(CreateUserResultTypes.USER_NOT_FOUND);
          }
        } else {
          setView(CreateUserResultTypes.USER_CREATE_FAILED);
          console.warn('[forgotPin response]:', res);
        }
      })
      .catch(error => {
        console.warn(error);
        setView(CreateUserResultTypes.SYSTEM_ERROR);
      });
  };

  const handlePartnerChanged = ({ currentTarget }: React.SyntheticEvent<HTMLSelectElement>) => {
    const { value } = currentTarget;
    const selectedPartner = partnerList.find((partner) => String(partner.partnerId) === value);
    if (selectedPartner) {
      setPartnerInfo({
        partnerNumber: String(selectedPartner.partnerId),
        hostname: selectedPartner.hostname,
        platform: selectedPartner.platform,
        name: selectedPartner.name,
      });
    }
    setFormStatus(FormStatus.INITIAL);
    setValidationFeedback(state => ({
      ...state,
      partner: selectedPartner === undefined ? partnerNumberInvalid : '',
    }));
  };

  const handleCustomerNumber = ({ currentTarget }: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = currentTarget;
    setFormSubmitionValues(state => ({ ...state, customerNumber: value }));
    setFormStatus(FormStatus.INITIAL);
    setValidationFeedback(state => ({
      ...state,
      customerNumber: !isEmpty(value) && isOnlyNumbers(value) ? '' : customerNumberInvalid,
    }));
  };

  const handleBack = () => {
    setFormSubmitionValues({
      mobileNumber: '',
      email: '',
      partnerNumber: '',
      customerNumber: '',
      pin: '',
    });
    setPartnerInfo({} as PartnerInfo);
    setView(CreateUserResultTypes.USER_CREATE);
  };

  return (
    <>
    {formStatus === FormStatus.SUBMITTING && <Spinner textConfig={textConfig}/>}
    {formStatus !== FormStatus.SUBMITTING && (
    <>
    <CardHeader title={title} ingress={ingress} emoji={emoji} emojiLabel={emojiLabel} />
    <CardBody>
      <form onSubmit={handleFormSubmit}>
        <PartnerSelect
          textConfig={textConfig}
          partnerList={partnerList}
          partnerInfo={partnerInfo}
          handlePartnerChanged={handlePartnerChanged}
          showValidationError={validationFeedback.partner !== '' && formStatus === FormStatus.TRIED_SUBMITTING}
        />

        <div className="form__field">
          <label htmlFor="customernumber" className="form__field__label">
            {formCustomerNumberLabel}
          </label>
          <input
            onChange={handleCustomerNumber}
            type="text"
            pattern="[0-9]*"
            inputMode="numeric"
            id="customernumber"
            className="form__field__input"
            aria-describedby="customernumberFeedback"
            aria-invalid={ariaInvalid(formStatus, validationFeedback.customerNumber)}
            value={formSubmitionValues.customerNumber}
          />
          <div className="form__field__feedback">
            {(formStatus === FormStatus.TRIED_SUBMITTING && validationFeedback.customerNumber !== '') &&
              <div className="form__field__feedback__text" id="customernumberFeedback">{textConfig.forgotPin.invalidCustomerNumber}</div>}
          </div>
        </div>

        <div className="form__field">
          <div className="form__field__submit">
            <button type="submit" id="wp-submit-forgot-pin" className="form__field__submit__button">
              {buttonText}
            </button>
          </div>
        </div>

        {formStatus === FormStatus.TRIED_SUBMITTING && (validationFeedback.customerNumber !== '' || validationFeedback.partner !== '') && (
          <ValidationSummary textConfig={textConfig} validationFeedback={validationFeedback} />
        )}
      </form>
    </CardBody>
    <CardFooter>
        <button id="wp-" className="btn-link" onClick={handleBack}>{back}</button>
    </CardFooter>
    </>
    )}
    </>
  );
};
